import React from 'react';
import { DailySchedule as DailyScheduleType, Problem } from '../types/problem';
import { dueStatus, plural, relativeDays } from '../utils/format';
import Icon from './Icon';

interface DailyScheduleProps {
  schedule: DailyScheduleType;
  problems: Problem[];
  onStartDaily: () => void;
  onCustomSession: () => void;
  onOpenSettings: () => void;
}

function ProblemRow({ problem }: { problem: Problem }) {
  const status = dueStatus(problem.nextReview, problem.revisions.length);
  const reviews = problem.revisions.length;
  const meta = reviews === 0
    ? 'Not reviewed yet'
    : `Reviewed ${plural(reviews, 'time')} · every ${plural(problem.interval, 'day')}`;

  return (
    <li>
      <a className="list-row" href={problem.url} target="_blank" rel="noopener noreferrer">
        <div className="list-main">
          <div className="list-title">{problem.title}</div>
          <div className="list-meta">{meta}</div>
        </div>
        <span className={`badge badge-${status.tone}`}>{status.label}</span>
        <span className="list-icon"><Icon name="external" size={14} /></span>
      </a>
    </li>
  );
}

export default function DailySchedule({
  schedule, problems, onStartDaily, onCustomSession, onOpenSettings,
}: DailyScheduleProps) {
  const { problems: today, totalDue, totalProblems, problemsPerDay, cycleDays } = schedule;
  const count = today.length;
  const backlog = totalDue - count;

  const todayIds = new Set(today.map(p => p.id));
  const upcoming = problems
    .filter(p => !todayIds.has(p.id) && p.nextReview > Date.now())
    .sort((a, b) => a.nextReview - b.nextReview)
    .slice(0, 5);

  const dateLabel = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="eyebrow">{dateLabel}</div>
          <h1 className="page-title">Today's revisions</h1>
          <p className="page-subtitle">
            {count > 0
              ? `${plural(count, 'problem')} queued for today. About 5 minutes each.`
              : 'Nothing due right now. Nice work.'}
          </p>
        </div>
        <button className="btn btn-primary btn-lg" onClick={onStartDaily} disabled={count === 0}>
          <Icon name="play" size={14} />
          Start session
        </button>
      </div>

      <div className="stats">
        <div className="card stat">
          <div className="stat-label">Today</div>
          <div className="stat-value tone-accent">{count}</div>
          <div className="stat-hint">in this session</div>
        </div>
        <div className="card stat">
          <div className="stat-label">Due</div>
          <div className={`stat-value ${totalDue > 0 ? 'tone-warning' : ''}`}>{totalDue}</div>
          <div className="stat-hint">including backlog</div>
        </div>
        <div className="card stat">
          <div className="stat-label">Library</div>
          <div className="stat-value">{totalProblems}</div>
          <div className="stat-hint">saved problems</div>
        </div>
        <div className="card stat">
          <div className="stat-label">Daily target</div>
          <div className="stat-value">{problemsPerDay}</div>
          <div className="stat-hint">
            <button className="btn-link" onClick={onOpenSettings}>{cycleDays}-day cycle</button>
          </div>
        </div>
      </div>

      {backlog > 0 && (
        <div className="alert alert-warning" style={{ marginBottom: 16 }}>
          <Icon name="info" />
          <div>
            <strong>{plural(backlog, 'more problem')} overdue.</strong> They'll roll into the next few days so
            each session stays a manageable size.
          </div>
        </div>
      )}

      <section className="card">
        <div className="card-header">
          <div>
            <div className="card-title">Queue</div>
            <div className="card-desc">Highest priority first: overdue, new, then rarely reviewed.</div>
          </div>
        </div>
        {count > 0 ? (
          <ul className="list">
            {today.map(p => <ProblemRow key={p.id} problem={p} />)}
          </ul>
        ) : (
          <div className="empty">
            <div className="empty-icon"><Icon name="check" size={22} /></div>
            <div className="empty-title">You're all caught up</div>
            <p>
              {upcoming.length > 0
                ? `Next review ${relativeDays(upcoming[0].nextReview)}.`
                : 'Solve a problem on LeetCode and save it with the extension to get started.'}
            </p>
          </div>
        )}
        <div className="card-footer">
          <span className="next-hint">Want to review something else?</span>
          <span className="spacer" />
          <button className="btn btn-secondary" onClick={onCustomSession}>Custom practice</button>
          <button className="btn btn-primary" onClick={onStartDaily} disabled={count === 0}>
            Start session <Icon name="arrowRight" size={14} />
          </button>
        </div>
      </section>

      {upcoming.length > 0 && (
        <section className="card">
          <div className="card-header">
            <div className="card-title">Coming up</div>
          </div>
          <ul className="list">
            {upcoming.map(p => <ProblemRow key={p.id} problem={p} />)}
          </ul>
        </section>
      )}
    </div>
  );
}
